import React from 'react';
import styled from 'styled-components';
import { Photo } from '../types/Photo';
import ProtectedImage from './ProtectedImage';

interface PhotoCardProps {
  photo: Photo;
  onClick?: () => void;
}

const Card = styled.div`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  cursor: pointer;
  background: white;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  height: 300px;
  overflow: hidden;
  background: #f0f0f0;
`;

const StyledProtectedImage = styled(ProtectedImage)`
  width: 100%;
  height: 100%;
`;

const Content = styled.div`
  padding: 16px;
`;

const Title = styled.h3`
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 600;
  color: #333;
`;

const Location = styled.p`
  margin: 0 0 8px 0;
  font-size: 14px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 4px;

  &::before {
    content: '📍';
  }
`;

const Date = styled.p`
  margin: 0;
  font-size: 14px;
  color: #999;
`;

const Description = styled.p`
  margin: 12px 0 0 0;
  font-size: 14px;
  color: #555;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PhotoCard: React.FC<PhotoCardProps> = ({ photo, onClick }) => {
  const formatDate = (date: Date | string): string => {
    const dateObj = typeof date === 'string' ? new (Date as any)(date) : date;
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Card onClick={onClick}>
      <ImageContainer>
        <StyledProtectedImage
          src={photo.imageURL}
          alt={photo.title}
          loading="lazy"
          showWatermark={false}
        />
      </ImageContainer>
      <Content>
        <Title>{photo.title}</Title>
        <Location>{photo.location}</Location>
        <Date>{formatDate(photo.date)}</Date>
        {photo.description && <Description>{photo.description}</Description>}
      </Content>
    </Card>
  );
};

export default PhotoCard;
